import { cloneDeep } from '@apollo/client/utilities';
import { ProductIdentifierUtil } from '../util/product.util';
import { forCreate, forDelete, forUpdate, StockLocationEntity } from './common/entity';
import { HandlingUnit } from './handling_unit/handlingUnit';
import { ProductTransaction, ProductTransactionType } from './productTransaction';
import { CustomField, CustomFieldValue } from './custom_field';

export class Product extends StockLocationEntity {
  productMasterDataId!: string;
  stockLocationId!: string;
  binId!: string;
  lotId?: string;
  containerId?: string;
  serial?: string;
  lpn?: string;
  unitQuantity!: string;
  identifier?: string;
  kitId?: string;

  constructor(obj: any) {
    if (!obj.companyId || !obj.stockLocationId) return;
    super(obj.companyId, obj.stockLocationId);
    Object.assign(this, cloneDeep(obj));
  }

  forUpdate(): UpdateProductInput {
    this.validate();
    delete (this as any).customFields;
    return UpdateProductInput.from(this, UpdateProductInput);
  }

  forDelete(): DeleteProductInput {
    this.validate();
    return DeleteProductInput.from(this, DeleteProductInput);
  }

  validate() {
    return this.validateEntity(Object.getOwnPropertyNames(this) as (keyof Product)[], (field: keyof Product) => {
      return null;
    });
  }

  getIdentifierValue(id: string) {
    if (!this.identifier) return undefined;
    return ProductIdentifierUtil.getIdentifierValueById(this.identifier, id);
  }

  getAllIdentifierValues() {
    return ProductIdentifierUtil.getAllIdentifierValues(this.identifier);
  }

  static fromIdentifier(customFields: Map<string, any>, identifier?: string): CustomFieldValue[] {
    if (!identifier) return [];

    const items = identifier.split('::');
    if (items.length < 2) throw new Error('Invalid identifier format');

    const customFieldValues: CustomFieldValue[] = [];
    for (let i = 0; i < items.length; i += 2) {
      if (i + 1 < items.length) {
        const id = items[i];
        const cf = customFields.get(id);
        if (!cf) continue;

        // Assuming CustomFieldValue.fromCustomField exists and works similarly
        customFieldValues.push(CustomField.toValue(cf, items[i + 1]));
      }
    }
    return customFieldValues;
  }

  static forHandlingUnit(product: Product, hu: HandlingUnit) {
    return new Product({
      ...product,
      unitQuantity: (BigInt(product.unitQuantity) * BigInt(hu.quantity)).toString(),
      productMasterDataId: hu.productMasterDataId,
    });
  }

  static forOutbound(transaction: ProductTransaction) {
    const product = transaction.product;

    return new Product({
      companyId: transaction.companyId,
      productMasterDataId: product.pmdId,
      stockLocationId: product.fromStockLocationId,
      binId: product.fromBinId,
      lotId: product.lotId,
      containerId: product.fromContainerId,
      serial: product.serialNbr,
      lpn: product.lpn,
      identifier: ProductIdentifierUtil.createIdentifierString(
        new Map(
          Array.from(
            transaction.type === ProductTransactionType.inbound
              ? product.toCustomFields.values()
              : product.fromCustomFields.values(),
          ).map(cf => [cf.id, cf.value]),
        ),
      ),
      kitId: product.kit?.fromKitId,
    });
  }

  static hash(product: Product) {
    return `${product.productMasterDataId}|${product.stockLocationId}|${product.binId}|${product.lotId || ''}|${
      product.containerId || ''
    }|${product.serial || ''}|${product.lpn || ''}|${product.identifier || ''}|${product.kitId || ''}`;
  }
}

export class CreateProductInput extends forCreate(Product) {}

export class UpdateProductInput extends forUpdate(Product) {}

export class DeleteProductInput extends forDelete(Product) {}

export enum GS1ApplicationIdentifier {
  serialShippingContainerCode = '00',
  globalTradeItemNumber = '01',
  globalTradeItemNumberOfTradeItemsContained = '02',
  lotNumber = '10',
  productionDate = '11',
  dueDate = '12',
  packagingDate = '13',
  bestBeforeDate = '15',
  expirationDate = '17',
  internalProductVariant = '20',
  serialNumber = '21',
  consumerProductVariant = '22',
  additionalProductIdentificationAssignedByTheManufacturer = '240',
  customerPartNumber = '241',
  madeToOrderVariationNumber = '242',
  secondarySerialNumber = '250',
  referenceToSourceEntity = '251',
  globalDocumentTypeIdentifier = '253',
  globalLocationNumberExtensionComponent = '254',
  globalCouponNumber = '255',
  variableCountOfItems = '30',
  countOfTradeItemsContained = '37',
  customerPurchaseOrderNumber = '400',
  globalIdentifierNumberForConsignment = '401',
  globalShipmentIdentificationNumber = '402',
  routingCode = '403',
  shipToDeliverToGlobalLocationNumber = '410',
  billToInvoiceToGlobalLocationNumber = '411',
  purchasedFromGlobalLocationNumber = '412',
  shipForDeliverForForwardToGlobalLocationNumber = '413',
  globalLocationNumber = '414',
  globalLocationNumberInvoicingParty = '415',
  shipToDeliverToPostalCode = '420',
  shipToDeliverISOcountryCode = '421',
  countryOfOriginOfTradeItem = '422',
  countryOfInitialProcessing = '423',
  countryOfProcessing = '424',
  countryOfDisassembly = '425',
  countryCoveringFullProcessChain = '426',
  natoStockNumber = '7001',
  uneceMeatCarcassesAndCutsClassification = '7002',
  expirationDateAndTime = '7003',
  rollProducts = '8001',
  cellularMobileTelephoneIdentifier = '8002',
  globalReturnableAssetIdentifier = '8003',
  globalIndividualAssetIdentifier = '8004',
  pricePerUnitOfMeasure = '8005',
  identificationOfIndividualTradeItem = '8006',
  internationalBankAccountNumber = '8007',
  dateAndTimeOfProduction = '8008',
  componentPartIdentifier = '8010',
  componentPartIdentifierSerialNumber = '8011',
  softwareVersion = '8012',
  globalModelNumber = '8013',
  globalServiceRelationNumberProvider = '8017',
  globalServiceRelationNumberRecipient = '8018',
  paymentSlipReferenceNumber = '8020',
  couponCodeIdentificationNorthAmerica = '8110',
  informationMutuallyAgreedBetweenTradingPartners = '90',
  companyInternalInformationExtra = '91',
  companyInternalInformationExtra2 = '92',
  companyInternalInformationExtra3 = '93',
  companyInternalInformationExtra4 = '94',
  companyInternalInformationExtra5 = '95',
  companyInternalInformationExtra6 = '96',
  companyInternalInformationExtra7 = '97',
  companyInternalInformationExtra8 = '98',
  companyInternalInformationExtra9 = '99',
  unknown = '-1',
}
